import React, { Component } from 'react';
import { routes } from 'constants/config';
import { Card } from 'ui/';
import request from 'utils/request_ajax';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import FeatureFlag from 'components/shared/FeatureFlag';
import classNames from 'classnames';
import AttendanceStatus from 'components/InPersonTraining/EventRegistrationOverview/AttendanceStatus';
import Loading from 'components/shared/Loading';
import EventNotFoundGraphic from 'components/shared/EventNotFoundGraphic';
import Header from './Header';
import EventsDashboardBackLink from './EventsDashboardBackLink';
import AttendanceForm from './AttendanceForm';
import SessionListing from './SessionListing';

class EventRegistration extends Component {
  state = {
    loading: true,
    event: null,
    sessionWithRelevantAttendance: null,
    sessions: [],
    attendance: null,
    selectedSessionId: null,
    changingAttendance: false,
    meta: {
      policies: {
        can_change_attendance: null,
      },
      currentUser: {
        id: null,
      },
    },
    type: {
      past_event: null,
      multi_session: null,
    },
  };

  getEventRegistration = eventId => {
    request
      .get(routes.IN_PERSON_TRAINING.ATTENDEE.EVENT_REGISTRATION(eventId))
      .then(payload => {
        // Single session selectedSessionId starts as only available session id
        // Multi session selectedSessionId starts as default null value
        const { sessions } = payload;
        const selectedSessionId = !payload.type.multi_session
          ? sessions?.[0]?.id
          : null;

        this.setState({
          ...payload,
          selectedSessionId,
          sessionWithRelevantAttendance: this.relevantSession(sessions),
          changingAttendance: false,
          loading: false,
        });
      });
  };

  componentDidMount() {
    const eventId = window.location.href.match(
      /in_person_training\/attendee\/events\/(\d+)\//,
    )[1];

    this.getEventRegistration(eventId);
  }

  onSessionClick = selectedSessionId => {
    this.setState({ selectedSessionId });
  };

  isEventAtCapacity = () => {
    return this.state.sessions.every(session => session.type.at_capacity);
  };

  relevantSession = sessions => {
    let relevantSession;
    const positiveStatus = sessions?.find(session =>
      ['did_attend', 'will_attend'].includes(session.attendance?.status),
    );
    if (positiveStatus) {
      relevantSession = positiveStatus;
    } else {
      relevantSession =
        sessions?.find(session =>
          ['did_not_attend', 'will_not_attend'].includes(
            session.attendance?.status,
          ),
        ) || sessions?.[0];
    }
    return relevantSession;
  };

  hasRsvpInfo = () => {
    const { changingAttendance, sessionWithRelevantAttendance } = this.state;
    return (
      ![null, 'awaiting_reply'].includes(
        sessionWithRelevantAttendance?.attendance?.status,
      ) && !changingAttendance
    );
  };

  showAttendanceStatus = () => {
    if (this.state.loading) {
      return false;
    }

    const { meta } = this.state;
    const { past_event: pastEvent } = this.state.type;

    const pastEventUneditable =
      pastEvent && !meta.policies.can_change_attendance;

    return (
      this.hasRsvpInfo() || pastEventUneditable || this.isEventAtCapacity()
    );
  };

  showStatusInFooter = () => {
    const rsvpSingleWithPositiveStatus =
      this.state.event?.rsvp_type === 'single' &&
      ['did_attend', 'will_attend'].includes(
        this.relevantSession()?.attendance?.status,
      );
    return this.hasMultipleSessions() && !rsvpSingleWithPositiveStatus;
  };

  showAttendanceForm = () => {
    if (this.state.loading) {
      return false;
    }

    const { can_change_attendance: canChangeAttendance } =
      this.state.meta.policies;
    const { past_event: pastEvent } = this.state.type;

    if (canChangeAttendance) {
      return true;
    }
    return !pastEvent;
  };

  onChangeAttendance = () => {
    this.setState({
      changingAttendance: true,
    });
  };

  onUpdateAttendance = attendanceStatus => {
    const { event, meta, selectedSessionId, type } = this.state;
    const status = type.past_event
      ? { attendanceStatus: attendanceStatus ? 'did_attend' : 'did_not_attend' }
      : { rsvpStatus: attendanceStatus ? 'will_attend' : 'will_not_attend' };
    const url =
      event.rsvp_type === 'single' && selectedSessionId
        ? routes.TRAINING.SESSION_SET_ATTENDANCE(event.id, selectedSessionId)
        : routes.TRAINING.EVENT_SET_RSVP(event.id);
    request
      .put(url, {
        ...status,
        userId: meta.currentUser.id,
        createdFrom: null,
      })
      .then(payload => {
        if (payload.data) {
          this.getEventRegistration(event.id);
        }
      });
  };

  // @todo Stub until we begin saving data
  displayRsvpConfirmation = () => {
    return false;
  };

  // @todo Stub until we begin saving data
  displayRsvpRejection = () => {
    return false;
  };

  showRsvpButtons = () => {
    const hasAttendance = this.hasRsvpInfo();
    const { meta, changingAttendance } = this.state;
    const { past_event: pastEvent, multi_session: multiSession } =
      this.state.type;

    if (multiSession && !hasAttendance) {
      return true;
    }

    const neverRegistered =
      !hasAttendance && (!pastEvent || meta.policies.can_change_attendance);

    return (neverRegistered || changingAttendance) && !this.isEventAtCapacity();
  };

  showAddToCalendar = () => {
    const { sessionWithRelevantAttendance, type } = this.state;
    return (
      sessionWithRelevantAttendance?.attendance?.status === 'will_attend' &&
      !type.past_event
    );
  };

  hasMultipleSessions = () => {
    return this.state.type.multi_session;
  };

  unconfirmedRsvpForFutureMultisession = () => {
    return (
      this.hasMultipleSessions() &&
      !this.state.type.past_event &&
      !this.hasRsvpInfo()
    );
  };

  render() {
    const {
      loading,
      event,
      sessions,
      selectedSessionId,
      sessionWithRelevantAttendance,
      attendance,
      changingAttendance,
    } = this.state;

    const totalSessions = sessions.length;
    const archivedOrDeleted =
      !event || !!event?.archived_at || !!event?.deleted_at;

    return (
      <div className="EventRegistration">
        <Loading loading={loading}>
          {archivedOrDeleted && <EventNotFoundGraphic isLearner />}
          {!archivedOrDeleted && (
            <>
              <EventsDashboardBackLink />
              <Header {...event} />
              <Card>
                <Card.Content padding="large">
                  <div className="d-f fd-c">
                    <Text
                      className={classNames({
                        'EventRegistration-details': event?.rsvp_type === 'all',
                      })}
                      variant={TEXT_VARIANTS.SUBHEADER_BOLD}
                    >
                      {t('in_person_training.event_details')}
                    </Text>

                    {this.unconfirmedRsvpForFutureMultisession() &&
                      event?.rsvp_type === 'single' && (
                        <div className="MySessionListDescription">
                          <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                            {t(
                              'in_person_training.events.register.select_a_session',
                            )}
                          </Text>
                          <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                            {t('in_person_training.events.number_of_sessions', {
                              count: totalSessions,
                            })}
                          </Text>
                        </div>
                      )}

                    {this.hasMultipleSessions() && event?.rsvp_type === 'all' && (
                      <div className="mt-8 ml-16 d-f jc-sb">
                        <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                          {t(
                            'in_person_training.events.register.includes_sessions',
                            { count: sessions.length },
                          )}
                        </Text>
                      </div>
                    )}

                    <FeatureFlag features="events_in_development">
                      {this.displayRsvpConfirmation() && (
                        <Text
                          className="mt-16 d-b"
                          variant={TEXT_VARIANTS.BODY_LIGHT}
                        >
                          {t(
                            'in_person_training.rsvp_status.confirmation_message',
                          )}
                        </Text>
                      )}
                    </FeatureFlag>

                    {sessionWithRelevantAttendance && (
                      <div className="mt-20">
                        <SessionListing
                          sessions={sessions}
                          selectedSessionId={selectedSessionId}
                          handleSessionClick={this.onSessionClick}
                          hasMultipleSessions={this.hasMultipleSessions()}
                          attendance={sessionWithRelevantAttendance.attendance}
                          currentSession={sessionWithRelevantAttendance}
                          event={event}
                          changingAttendance={changingAttendance}
                          canChangeAttendance={
                            this.state.meta.policies.can_change_attendance
                          }
                        />
                      </div>
                    )}

                    {this.showAttendanceStatus() &&
                      !this.showStatusInFooter() &&
                      sessionWithRelevantAttendance && (
                        <div className="mt-16">
                          <AttendanceStatus
                            attendance={
                              sessionWithRelevantAttendance.attendance
                            }
                            pastEvent={this.state.type.past_event}
                            showSessionAtCapacityNotice={this.isEventAtCapacity()}
                          />
                        </div>
                      )}

                    <FeatureFlag features="events_in_development">
                      {this.displayRsvpRejection() && (
                        <Text
                          className="fs-i mt-16 d-b"
                          variant={TEXT_VARIANTS.BODY}
                        >
                          {t(
                            'in_person_training.rsvp_status.will_not_attend_reminder',
                          )}
                        </Text>
                      )}
                    </FeatureFlag>
                  </div>
                </Card.Content>
                <Card.Content className="bg-gray-lightest bt-gray-lighter px-24 py-16">
                  <div className="Event Registration RsvpTypeAll-buttons">
                    {this.showAttendanceStatus() &&
                      this.showStatusInFooter() &&
                      sessionWithRelevantAttendance && (
                        <AttendanceStatus
                          attendance={sessionWithRelevantAttendance.attendance}
                          isRsvpTypeAll
                          pastEvent={this.state.type.past_event}
                          showSessionAtCapacityNotice={this.isEventAtCapacity()}
                        />
                      )}
                    {this.showAttendanceForm() &&
                      sessionWithRelevantAttendance && (
                        <AttendanceForm
                          showRsvpButtons={this.showRsvpButtons}
                          pastEvent={this.state.type.past_event}
                          handleUpdateAttendance={this.onUpdateAttendance}
                          handleChangeAttendance={this.onChangeAttendance}
                          canChangeAttendance={
                            this.state.meta.policies.can_change_attendance
                          }
                          eventAtCapacity={this.isEventAtCapacity()}
                          showAddToCalendar={this.showAddToCalendar}
                          event={event}
                          session={sessionWithRelevantAttendance}
                          attendance={attendance}
                          selectedSessionId={selectedSessionId}
                          hasMultipleSessions={this.hasMultipleSessions()}
                        />
                      )}
                  </div>
                </Card.Content>
              </Card>
            </>
          )}
        </Loading>
      </div>
    );
  }
}

export default EventRegistration;
